import { useEffect, useRef, useState } from 'react';
import { useSelector } from 'react-redux';
import { useNavigate } from 'react-router-dom';
import { toast } from 'sonner';
import type { RootState } from '@/reducers';
import { useGetRemindersQuery } from '@/slices/remindersApi';
import { alertMessage, alertWindow, arrivedAlerts } from './reminderToasts';

const POLL_INTERVAL_MS = 3 * 60 * 1000;
const CHECK_INTERVAL_MS = 30 * 1000;
// The server caps a page at this; the window keeps it far below that.
const REMINDER_LIMIT = 500;

/**
 * Headless component that polls for the reminders that notify the current user
 * and surfaces a toast at each of their alert times. Renders nothing.
 */
const ReminderAlerts = () => {
	const navigate = useNavigate();
	const userId = useSelector((state: RootState) => state.auth?.user?._id);
	const channel = useSelector(
		(state: RootState) =>
			state.auth?.user?.workflowSettings?.reminderNotificationChannel
	);

	// Alerts come due with no new data, so the clock has to drive the check too.
	const [now, setNow] = useState(() => Date.now());
	useEffect(() => {
		const tick = () => setNow(Date.now());
		const timer = setInterval(tick, CHECK_INTERVAL_MS);
		document.addEventListener('visibilitychange', tick);
		return () => {
			clearInterval(timer);
			document.removeEventListener('visibilitychange', tick);
		};
	}, []);

	const { data } = useGetRemindersQuery(
		{ status: 'open', limit: REMINDER_LIMIT, ...alertWindow(now) },
		{
			skip: !userId,
			pollingInterval: POLL_INTERVAL_MS,
			// Background tabs don't need due-reminder toasts; stop polling
			// until the tab is focused again.
			skipPollingIfUnfocused: true,
		}
	);

	// Tracks alerts we've already shown (or seen as arrived on load).
	const seenAlerts = useRef<Set<string>>(new Set());
	const initialized = useRef(false);

	useEffect(() => {
		if (!data?.reminders || !userId) return;

		const alerts = arrivedAlerts(data.reminders, { userId, channel, now });

		// On the first successful load, seed the seen set without toasting so
		// we don't replay every existing overdue reminder on page load.
		if (!initialized.current) {
			for (const { keys } of alerts) {
				for (const key of keys) seenAlerts.current.add(key);
			}
			initialized.current = true;
			return;
		}

		// A toast in a hidden tab would expire unseen; wait until it's back.
		if (document.hidden) return;

		const newAlerts = alerts.filter(({ keys }) =>
			keys.some((key) => !seenAlerts.current.has(key))
		);
		if (newAlerts.length === 0) return;

		for (const { keys } of newAlerts) {
			for (const key of keys) seenAlerts.current.add(key);
		}

		toast(alertMessage(newAlerts, now), {
			action: {
				label: 'View',
				onClick: () => navigate('/reminders'),
			},
		});
	}, [data, now, userId, channel, navigate]);

	return null;
};

export default ReminderAlerts;
